import { Button, Modal, Page, Spinner, Variant } from '@lib/components'
import { Currency, displayCurrency, displayTidyCurrency, getCurrencySymbol, isCampaignInCurrentYearOrPrevious, penniesToPound, poundsToPennies, symbols } from '@lib/services'
import { ActiveCampaign, canScheduleBeAutoEnrolled, Card, type Cause, fetchCheckoutSession, getPlatformStripe, GivematchShareSection, loadCharityCampaign, loadSchedule, MTDtracking, NotFoundError, type Payment, QurbaniCategory, saveSchedule, setCancelled, PaymentStatus as Status, updateAutoEnroll, type UpdateScheduleFormData, type Schedule as ScheduleType, calculateTotalFees, getMinimalMTDAmount, ScrollHash, scrollToHash, hasFirstPaymentPassed, saveQurbani, isWithinDhulHijjahWindow, hasTipPaymentBeenProcessed } from '@shamaazi/mytennights'
import { DateTime } from 'luxon'
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { useAuth } from '@lib/hooks'
import { ScheduleTestIds } from '@lib/testing'
import { QurbaniBanner } from '~/components/QurbaniBanner'
import { Controller, useForm } from 'react-hook-form'
import { QurbaniInput, QurbaniWidget } from '~/components/QurbaniWidget'
import { charityHasQurbaniCause, getScheduleNonQurbaniCauses, getScheduleQurbaniCauses, hasQurbaniPaymentBeenProcessed, scheduleHasQurbaniCause } from '~/service/qurbani'
import CurrencyInput from 'react-currency-input-field'

const getStatusColor = (status: Status): { bg: string, text: string } => {
  if (status === Status.Skipped) {
    return { bg: 'bg-mtd-blue-200', text: 'text-mtd-blue-200' }
  }
  if (status === Status.Cancelled) {
    return { bg: 'bg-mtd-gray-700', text: 'text-mtd-red' }
  }
  if (status === Status.Failed) {
    return { bg: 'bg-mtd-red', text: 'text-mtd-red' }
  }
  if (status === Status.Succeeded) {
    return { bg: 'bg-mtd-blue', text: 'text-mtd-blue' }
  }
  return { bg: 'bg-mtd-gray-700', text: 'text-mtd-gray-700' }
}

const calculateTipRatio = (payments: Payment[], tip: number): number => {
  if (tip === 0) {
    return 0
  }
  let totalDonation = 0
  for (const payment of payments) {
    for (const cause of payment.breakdown.causes) {
      totalDonation += cause.amount
    }
  }
  const ratio = tip / totalDonation
  return Number(ratio.toFixed(2))
}

const calculateTip = (causes: Record<string, number>, tipRatio: number): number => {
  const totalDonation = Object.values(causes).reduce((sum, value) => sum + value, 0)
  const tip = totalDonation * tipRatio
  return Number(tip.toFixed(0))
}

export const Schedule: FC = () => {
  const { scheduleID } = useParams() as { scheduleID: string }
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [userHasHiddenBanner, setUserHasHiddenBanner] = useState(false)
  const [searchParams] = useSearchParams()
  const updatePaymentMethod = searchParams.get('update') === 'payment_method'
  const [showQurbaniModal, setShowQurbaniModal] = useState(false)
  const [tipRatio, setTipRatio] = useState(0)
  const [minimumAmountError, setMinimumAmountError] = useState('')

  // When the tip has been collected, do not allow the tip to be reduced (as it may have already been processed)
  const [reducedTipError, setReducedTipError] = useState('')

  // Forms
  const updateForm = useForm<UpdateScheduleFormData>()
  const updateFormWatch = updateForm.watch()
  const causeAmounts = updateForm.watch('cause_amounts')
  const formTip = updateForm.watch('tip_amount')
  interface QurbaniForm {
    causes: Record<string, number>
  }
  const qurbaniForm = useForm<QurbaniForm>({
    defaultValues: {
      causes: {}
    }
  })
  const qurbaniCauses = qurbaniForm.watch('causes')

  // Queries
  const { isLoading, isError, error, data: schedule } = useQuery<ScheduleType>(['schedule', scheduleID],
    async () => {
      const schedule = await loadSchedule(scheduleID)

      // Set default form values and tip ratio on loading schedule
      const defaultValues: Record<string, number> | undefined = schedule.causes.reduce((obj, item) => ({
        ...obj,
        [item.cause_id]: item.amount
      }), {})
      updateForm.reset({
        campaign: schedule.campaign,
        cause_amounts: defaultValues,
        tip_amount: schedule.tip,
        pay_fees: schedule.payFees
      })
      setTipRatio(calculateTipRatio(schedule.payments, schedule.tip))

      return schedule
    }, { retry: 0 })

  const { isLoading: isLoadingCharity, error: charityError, data: charity } = useQuery(
    ['charity', schedule?.charity_id],
    async () => loadCharityCampaign(ActiveCampaign.myTenDays, schedule!.charity_id),
    {
      // Charity query only runs when schedule is loaded and has a valid charity_id
      enabled: !!schedule?.charity_id
    }
  )

  // Utils to handle API calls
  const { mutate: onSaveScheduleSubmit, isError: isSaveScheduleError, isLoading: isSaveScheduleLoading } = useMutation(
    async (formData: UpdateScheduleFormData) => {
      // The update schedule API takes cause amounts in pounds (/larger denomination) rather than pennies
      const causeAmountsInPounds: Record<string, number> = {}
      for (const [causeID, amount] of Object.entries(formData.cause_amounts)) {
        causeAmountsInPounds[causeID] = Number(penniesToPound(amount).toFixed(2))
      }
      const tipInPounds = Number(penniesToPound(formData.tip_amount).toFixed(2))

      return await saveSchedule(scheduleID, { campaign: formData.campaign, cause_amounts: causeAmountsInPounds, tip_amount: tipInPounds, pay_fees: formData.pay_fees })
    },
    {
      onSuccess: (data) => {
        onSuccessSaveSchedule(data)
      }
    })

  const { mutate: onSaveQurbaniSubmit, isError: isSaveQurbaniError, isLoading: isSaveQurbaniLoading } = useMutation(
    async (formData: UpdateScheduleFormData) => {
      // The update schedule API takes cause amounts in pounds (/larger denomination) rather than pennies
      const causeAmountsInPounds: Record<string, number> = {}
      for (const [causeID, amount] of Object.entries(formData.cause_amounts)) {
        causeAmountsInPounds[causeID] = Number(penniesToPound(amount).toFixed(2))
      }
      const tipInPounds = Number(penniesToPound(formData.tip_amount).toFixed(2))

      // payFees always true for Qurbani
      const payFees = true

      // filter for qurbani causes only
      const qurbaniCauses = Object.fromEntries(Object.entries(causeAmountsInPounds).filter(([causeID, _]) => charityCauses![causeID].category === QurbaniCategory))

      return await saveQurbani(scheduleID, { campaign: formData.campaign, cause_amounts: qurbaniCauses, tip_amount: tipInPounds, pay_fees: payFees })
    },
    {
      onSuccess: (data) => {
        onSuccessSaveSchedule(data)
      }
    })

  const onSuccessSaveSchedule = (data: ScheduleType): void => {
    const scheduleCauses = data.causes.reduce((obj, item) => ({
      ...obj,
      [item.cause_id]: item.amount
    }), {})
    MTDtracking.editDonation(user?.email, scheduleCauses, charityCauses!, data.tip, data.fee, data.total, data.currency, symbols[data.currency])
    // eslint-disable-next-line no-restricted-globals
    location.reload()
  }

  const submitAmounts = (): void => {
    // Validate the total donation amount of non-Qurbani causes
    const nonQurbaniCauseIds = Object.keys(getScheduleNonQurbaniCauses(schedule!, charityCauses!))
    const causesTotal = Object.entries(updateForm.getValues('cause_amounts')).filter(([causeID]) => nonQurbaniCauseIds.includes(causeID)).reduce((total, [_, amount]) => total + amount, 0)
    const isValid = getMinimalMTDAmount(schedule!.currency) <= causesTotal
    if (!isValid) {
      setMinimumAmountError(`Total amount entered for causes is too low, minimum amount is ${displayTidyCurrency(getMinimalMTDAmount(schedule!.currency), schedule!.currency)}`)
      scrollToHash(ScrollHash.lowAmountEntered)
      return
    }

    onSaveScheduleSubmit(updateForm.getValues())
  }

  const addQurbani = (): void => {
    onSaveQurbaniSubmit(updateForm.getValues())
  }

  const updateOrCancelQurbani = (): void => {
    if (!schedule) {
      throw new Error('Schedule not found')
    }
    setReducedTipError('')
    // If tip has already been processed, do not allow the user to lower the tip amount
    if (hasTipPaymentBeenProcessed(schedule) && updateForm.getValues('tip_amount') < schedule.tip) {
      setReducedTipError(`Tip cannot be lower than previous amount of ${displayCurrency({ amount: schedule.tip, currency: schedule.currency })}, because your tip has already been processed`)
      throw new Error('Tip lower than previous amount')
    }
    onSaveQurbaniSubmit(updateForm.getValues())
  }

  const { mutate: changeScheduleCancelled, isError: isCancelError, isLoading: isCancelLoading } = useMutation(
    async (): Promise<ScheduleType> => {
      const cancelled = !schedule!.cancelled
      if (cancelled) {
        MTDtracking.cancelDonation(user?.email, schedule!.charity_name, schedule!.charity_id, schedule!.total, schedule!.currency, schedule!.causes)
      } else {
        MTDtracking.reactivateDonation(user?.email, schedule!.charity_name, schedule!.charity_id, schedule!.total, schedule!.currency, schedule!.causes)
      }
      return await setCancelled(scheduleID, cancelled)
    },
    {
      onSuccess: (data) => {
        queryClient.setQueryData(['schedule', scheduleID], data)
        queryClient.invalidateQueries(['schedule', scheduleID]).catch(() => { })
      }
    })

  const { mutate: setAutoEnrollState, isError: isAutoEnrollCancelError, isLoading: isAutoEnrollCancelLoading } = useMutation(
    async (): Promise<ScheduleType> => await updateAutoEnroll(scheduleID, !(schedule?.auto_enroll ?? true)),
    {
      onSuccess: async (data: ScheduleType) => {
        queryClient.setQueryData(['schedules', scheduleID], data)
        await queryClient.invalidateQueries(['schedule', scheduleID])
      }
    }
  )

  const transactionFee = causeAmounts ? Number(calculateTotalFees(Object.values(causeAmounts).reduce((total, amount) => total + amount, 0), formTip, schedule!.payments.length, schedule!.currency).toFixed(0)) : 0

  const initialFormValues = useRef<UpdateScheduleFormData | null>(null)

  useEffect(() => {
    if (schedule) {
      const initialCauses = schedule.causes.reduce<Record<string, number>>((acc, { cause_id: causeID, amount }) => {
        acc[causeID] = amount
        return acc
      }, {})

      initialFormValues.current = {
        campaign: schedule.campaign,
        cause_amounts: initialCauses,
        tip_amount: schedule.tip,
        pay_fees: schedule.payFees
      }
    }
  }, [schedule])

  const [changesMade, setChangesMade] = useState<boolean>(false)

  useEffect(() => {
    if (!initialFormValues.current) return

    const current = updateForm.getValues()
    const initial = initialFormValues.current

    // Check if cause amounts have changed
    const causeAmountsDifferentLengths = Object.keys(initial.cause_amounts).length !== Object.keys(current.cause_amounts).length
    let causeAmountsChanged = false
    for (const causeID of Object.keys(current.cause_amounts)) {
      if (initial.cause_amounts[causeID] !== current.cause_amounts[causeID]) {
        causeAmountsChanged = true
      }
    }

    const otherChanges =
      initial.tip_amount !== current.tip_amount ||
      initial.pay_fees !== current.pay_fees

    setChangesMade(causeAmountsDifferentLengths || causeAmountsChanged || otherChanges)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateFormWatch, schedule])

  const charityCauses = useMemo<Record<string, Cause> | undefined>(() => {
    return charity ? charity.causes.reduce<Record<string, Cause>>((obj, { cause_id: causeID, ...rest }) => {
      obj[causeID] = { cause_id: causeID, ...rest }
      return obj
    }, {}) : undefined
  }, [charity])

  // Non-qurbani causes are only editable if the first payment has not been processed
  const areNonQurbaniCausesEditable = useMemo<boolean | undefined>(() => {
    if (!schedule) {
      return undefined
    }
    return !hasFirstPaymentPassed(schedule) && isWithinDhulHijjahWindow()
  }, [schedule])

  // User can add qurbani if there are causes available and the last payment has not been processed.
  const canQurbaniBeAdded = useMemo<boolean | undefined>(() => {
    if (!schedule || !charityCauses || !charity) {
      return undefined
    }
    return !scheduleHasQurbaniCause(schedule, charityCauses) && charityHasQurbaniCause(charity.causes) && isWithinDhulHijjahWindow() && schedule.campaign === ActiveCampaign.myTenDays
  }, [schedule, charityCauses, charity])

  // User can edit qurbani if the payment has not been processed.
  const isQurbaniEditable = useMemo<boolean | undefined>(() => {
    if (!schedule || !charityCauses) {
      return undefined
    }
    return scheduleHasQurbaniCause(schedule, charityCauses) && !hasQurbaniPaymentBeenProcessed(schedule, charityCauses)
  }, [schedule, charityCauses])

  const userCanOptOutOfTransactionFees = useMemo<boolean | undefined>(() => {
    if (!schedule || !charityCauses) {
      return undefined
    }
    const hasQurbaniCause = scheduleHasQurbaniCause(schedule, charityCauses)
    if (hasQurbaniCause) {
      updateForm.setValue('pay_fees', true)
    }
    return !hasQurbaniCause
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule, charityCauses])

  const onClickCancelQurbani = (): void => {
    if (!schedule || !charityCauses) {
      return
    }
    document.cookie = 'hideQurbaniBanner=true; expires=Thu, 18 Dec 2100 12:00:00 UTC; path=/'
    const newCauseAmounts = getScheduleNonQurbaniCauses(schedule, charityCauses)
    if (areNonQurbaniCausesEditable) {
      updateForm.setValue('tip_amount', calculateTip(newCauseAmounts, 0.1)) // Default to 10% on cancel qurbani
      updateForm.setValue('cause_amounts', newCauseAmounts)
      submitAmounts()
    } else {
      const qurbaniCauses = getScheduleQurbaniCauses(schedule, charityCauses)
      for (const causeID of Object.keys(qurbaniCauses)) {
        updateForm.setValue(`cause_amounts.${causeID}`, 0)
      }
      updateOrCancelQurbani()
    }
  }

  const handleClickUpdatePaymentDetails = useCallback(async (scheduleID: string, schedule: any): Promise<void> => {
    if (!schedule) {
      return
    }
    try {
      if (!user?.email) {
        return
      }
      // Call your backend to create the Checkout session.
      const sessionId: string = await fetchCheckoutSession(scheduleID, schedule, user.email)
      // When the customer clicks on the button, redirect them to Checkout.
      const stripe = await getPlatformStripe()
      const result = await stripe?.redirectToCheckout({ sessionId })
      if (result?.error) {
        console.error('Error redirecting to Checkout:', result.error)
      }
    } catch (error) {
      console.error('Error handling payment:', error)
    }
  }, [user?.email])

  const handleClickWrapper = useCallback((): void => {
    handleClickUpdatePaymentDetails(scheduleID, schedule).catch(error => {
      console.error('Error handling payment:', error)
    })
  }, [scheduleID, schedule, handleClickUpdatePaymentDetails])

  useEffect(() => {
    if (updatePaymentMethod) {
      handleClickWrapper()
    }
  }, [updatePaymentMethod, handleClickWrapper])

  const handleQurbaniCountChange = (delta: number, causeID: string): void => {
    if (!charityCauses) {
      return
    }
    const pricePerQuantity = charityCauses[causeID].price_per_quantity
    const currentCount = updateForm.getValues(`cause_amounts.${causeID}`) / pricePerQuantity

    const newAmount = (currentCount + delta) * pricePerQuantity
    updateForm.setValue(`cause_amounts.${causeID}`, newAmount)

    // Update tip in ratio to cause amounts
    updateForm.setValue('tip_amount', calculateTip(updateForm.getValues('cause_amounts'), tipRatio))

    // Transaction fees are always applied for schedules with Qurbani
    updateForm.setValue('pay_fees', true)
  }

  const onClickNoQurbaniBanner = (): void => {
    setUserHasHiddenBanner(true)
    MTDtracking.qurbaniBannerOptIn(user?.email, false)
  }

  const onClickYesQurbaniBanner = (): void => {
    setShowQurbaniModal(true)
    MTDtracking.qurbaniBannerOptIn(user?.email, true)
  }

  const onClickSave = (): void => {
    if (!areNonQurbaniCausesEditable && isQurbaniEditable) {
      updateOrCancelQurbani()
    } else {
      submitAmounts()
    }
  }

  const calculateTotal = (): number => {
    if (!schedule) {
      return 0
    }
    const causeAmounts = updateForm.getValues('cause_amounts')
    const tipAmount = formTip
    const payFees = updateForm.getValues('pay_fees')
    const total = causeAmounts ? Object.values(causeAmounts).reduce((total, amount) => total + amount, 0) + tipAmount : 0
    return payFees ? total + schedule.fee : total
  }

  const splitBreakdown = (schedule?.payments ?? []).map(({ number, total, date, status }) => {
    const color = getStatusColor(status)

    return <div key={number}
      className={`${status === Status.Skipped ? 'opacity-80' : ''} h-60 font-medium bg-mtd-gray-100 rounded flex flex-col items-center justify-center text-center`}>
      <span className={`${color.bg} h-14 w-14 rounded-full text-white text-xl flex items-center justify-center`}>{number}</span>
      <span className={`${color.text} text-xs mt-3`}>{date.toLocaleString({ weekday: 'short', month: 'long', day: 'numeric' })}</span>
      <span className={`${color.text} text-sm my-3`}>Payment {status}</span>
      <span className={color.text}>{status !== Status.Skipped ? displayCurrency({ amount: total, currency: schedule?.currency ?? Currency.GBP }) : 'Unavailable'}</span>
      {status !== Status.Skipped && <Link to={`/account/schedule/${scheduleID}/payment/${number}`} className="mt-2 text-xs underline"
        onClick={() => MTDtracking.viewScheduleDetails(user?.email)}>View Details</Link>}
    </div>
  })

  // Update causeAmounts with latest amounts from updateForm on open (i.e. reset the form)
  useEffect(() => {
    if (!showQurbaniModal) {
      qurbaniForm.reset()
    }
    qurbaniForm.setValue('causes', updateForm.getValues('cause_amounts'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showQurbaniModal])

  const qurbaniModal = (): JSX.Element => {
    const setFormCauses = (causes: Record<string, number>): void => {
      qurbaniForm.setValue('causes', causes)
    }

    const onSubmitQurbani = (): void => {
      updateForm.setValue('cause_amounts', qurbaniForm.getValues('causes'))

      // Update tip in ratio to cause amounts
      updateForm.setValue('tip_amount', calculateTip(qurbaniForm.getValues('causes'), tipRatio))

      // Transactional fees are always applied for schedules with Qurbani
      updateForm.setValue('pay_fees', true)

      if (schedule && hasFirstPaymentPassed(schedule)) {
        addQurbani()
      } else {
        submitAmounts()
      }
      setShowQurbaniModal(false)
    }

    return <Modal isOpen={showQurbaniModal} onClose={() => setShowQurbaniModal(false)} backgroundColor='bg-mtd-bg'>
      <div className='px-8 pb-8 mt-4 max-h-96 md:max-h-full overflow-auto flex flex-col gap-y-4'>
        {isLoading || isLoadingCharity ? <Spinner className="text-mtn-blue mt-2 mx-auto" /> : error ? <p className="font-medium text-center text-mtn-red">Sorry, we were unable to load any charities!</p> : <></>}

        {charity && qurbaniCauses && <div className='flex flex-col gap-y-10'>
          <QurbaniWidget charityCauses={charity.causes} setCauses={setFormCauses} checkoutCauses={qurbaniCauses} currency={charity.currency} />
        </div>}

        <div className='flex justify-between gap-x-4'>
          <Button className="w-full" variant="mtd-secondary-outline" onClick={() => setShowQurbaniModal(false)}>Cancel</Button>
          <Button className="w-full" variant="mtd-primary" onClick={onSubmitQurbani} disabled={isSaveScheduleLoading || isSaveQurbaniLoading}>Add To Schedule</Button>
        </div>
      </div>
    </Modal>
  }

  return <Page data-test-id={ScheduleTestIds.schedulePage} className="p-6 flex flex-col gap-y-10 bg-mtd-bg" >
    <Link className="underline" to="/account/schedules">&lt; Back to My Schedules</Link>

    {(charityError || isSaveScheduleError || isSaveQurbaniError) && <p className='text-center text-mtd-red'>Something went wrong. Please try again later</p>}

    {!userHasHiddenBanner && canQurbaniBeAdded && !document.cookie.includes('hideQurbaniBanner=true') &&
      <>
        <QurbaniBanner withActionButtons onClickYes={onClickYesQurbaniBanner} onClickNo={onClickNoQurbaniBanner} />
      </>
    }
    {qurbaniModal()}

    <div className="font-medium lg:grid lg:gap-x-20 lg:grid-cols-fr-auto">
      <div>
        <h1 className="text-xl text-mtd-blue">Schedule</h1>
        {(isLoading || isLoadingCharity) && <div className="flex justify-center mt-20">
          <Spinner />
        </div>}
        {isError && error instanceof NotFoundError && <div className="mt-10">
          <h2>Sorry, we couldn&apos;t find this donation.<br />Please check you are logged in as the correct email address</h2>
        </div>}
        {isError && !(error instanceof NotFoundError) && <div className="mt-10">
          <h2>Sorry, we weren't able to load your schedule. Please try again later and if
            the issue persists, <Link className="underline text-mtd-blue" to="/contact">contact us</Link>
          </h2>
        </div>}
        {!isLoading && !isSaveScheduleLoading && !isSaveQurbaniLoading && !isError && schedule !== undefined && charityCauses &&
        <>
          <p className="my-4 text-mtd-blue-900">Created
            at {schedule?.created?.toLocaleString(DateTime.TIME_24_SIMPLE)} on {schedule?.created?.toLocaleString(DateTime.DATE_MED)}</p>
          {schedule.cancelled && <p className="my-2 text-sm text-mtd-red">Cancelled</p>}
          <hr className="text-mtd-gray-300" />
          <section className="items-center my-10 grid gap-y-6 gap-x-10 grid-cols-auto-fr">
            <span className="text-sm">Charity</span>
            <span className="text-right text-mtd-blue-900">{schedule.charity_name}</span>

            {/* Regular donations */}
            <span className="self-start mt-2 text-sm">Causes</span>
            <div id={ScrollHash.lowAmountEntered}>
              {schedule.causes.filter(cause => charityCauses[cause.cause_id] ? charityCauses[cause.cause_id].category !== QurbaniCategory : true).map(c => <div key={c.name} className="mt-2 grid gap-x-6 grid-cols-fr-auto">
                <div className='flex justify-end items-center'><p className="text-right align-middle">{c.name}</p></div>
                {areNonQurbaniCausesEditable && causeAmounts
                  ? <div>
                    {getCurrencySymbol(schedule.currency)}
                    <CurrencyInput
                      className="ml-2 text-right w-44 p-3 border-mtd-gray-3 border"
                      id="causeAmount"
                      placeholder="0"
                      inputMode='decimal'
                      step={0.01}
                      value={penniesToPound(causeAmounts[c.cause_id] || 0)}
                      onValueChange={(val) => {
                        setMinimumAmountError('')
                        const pounds = parseFloat(val ?? '0')
                        updateForm.setValue(`cause_amounts.${c.cause_id}`, poundsToPennies(pounds)) // store in pennies
                      }}
                    />
                  </div>
                  : <span className="text-right text-mtd-blue-900">{displayCurrency({ amount: c.amount, currency: schedule.currency })}</span>
                }
              </div>)}
              {minimumAmountError && <p className="text-mtn-red-100 text-xs font-normal mt-2 text-right">{minimumAmountError}</p>}
            </div>

            {/* Qurbani donations */}
            {scheduleHasQurbaniCause(schedule, charityCauses) &&
              <>
                <span className="self-start mt-2 text-sm">Qurbani</span>
                <div className="flex flex-col gap-y-2">
                  {schedule.causes.filter(cause => charityCauses[cause.cause_id].category === QurbaniCategory).map(c => <div key={c.cause_id}>
                    {isQurbaniEditable && causeAmounts
                      ? <div key={c.cause_id} className="grid gap-x-4 grid-cols-fr-auto">
                        <div className="flex justify-end items-center">
                          <p>{charityCauses[c.cause_id].title}</p>
                          <p className="w-14 text-right font-bold">{displayCurrency({ amount: charityCauses[c.cause_id].price_per_quantity, currency: schedule.currency, decimalPlaces: 0 })}</p>
                        </div>
                        <div>
                          <QurbaniInput
                            causeID={c.cause_id}
                            count={causeAmounts[c.cause_id] / charityCauses[c.cause_id].price_per_quantity}
                            onCountChange={(causeID, delta) => handleQurbaniCountChange(delta, causeID)} />
                        </div>
                      </div>
                      : <div key={c.cause_id} className="mt-2 grid gap-x-6 grid-cols-fr-auto">
                        <span className="text-right">{c.name}</span>
                        <span className="text-right text-mtd-blue-900">{displayCurrency({ amount: c.amount, currency: schedule.currency })}</span>
                      </div>}
                  </div>)}
                </div>
              </>}

            {/* Tip */}
            <span className="text-sm">MyTenDays Donation</span>
            {
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
              (areNonQurbaniCausesEditable || isQurbaniEditable) ? <div className='flex justify-end items-center'>
              {getCurrencySymbol(schedule.currency)}
              <Controller
                name="tip_amount"
                control={updateForm.control}
                render={({ field }) => (
                  <CurrencyInput
                    className="ml-2 text-right w-44 p-3 border-mtd-gray-3 border"
                    id="causeAmount"
                    placeholder="0"
                    step={0.01}
                    value={penniesToPound(field.value || 0)}
                      onValueChange={(val) => {
                        const pounds = parseFloat(val ?? '0')
                        updateForm.setValue(`tip_amount`, poundsToPennies(pounds)) // store in pennies
                      }}
                  />)} /></div>
                : <span className="text-right text-mtd-blue-900">{displayCurrency({ amount: schedule.tip, currency: schedule.currency })}</span>}
              {reducedTipError && <><p></p> <div className="flex justify-end items-center">
               <p className="text-mtn-red-100 text-xs font-normal mt-2 text-right">{reducedTipError}</p>
              </div></>}

            {/* Transaction fees */}
            <span className="text-sm">Transaction Fees</span>
            <div className='flex justify-end items-center'>
              {areNonQurbaniCausesEditable && userCanOptOutOfTransactionFees &&
                <input
                  type="checkbox"
                  className="mr-6 sm:mr-4"
                  {...updateForm.register('pay_fees')} />}
              <span className="text-right text-mtd-blue-900">{
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                displayCurrency({ amount: (areNonQurbaniCausesEditable || isQurbaniEditable) ? transactionFee : schedule.fee, currency: schedule.currency })
              }</span>
            </div>

            <hr className="col-span-2 text-mtd-gray-200" />

            {/* Schedule total */}
            <span className="text-sm">Total</span>
            <span className="font-bold text-right text-mtd-blue-900">
              {
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
              displayCurrency({ amount: areNonQurbaniCausesEditable || isQurbaniEditable ? calculateTotal() : schedule.total, currency: schedule.currency })}
              </span>

            {/* Save button */}
            {
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
              (areNonQurbaniCausesEditable || isQurbaniEditable) && <>
                <span />
                <div className="w-full flex justify-end">
                  {changesMade ? <Button
                    variant="gm-widget"
                    onClick={onClickSave}
                    loading={isSaveScheduleLoading || isSaveQurbaniLoading} disabled={isSaveScheduleLoading || isSaveQurbaniLoading}>
                    Save
                  </Button>
                    : <p className="text-mtd-info text-mtd-secondary-3">All changes saved!</p>}
                </div>

              </>}
            {(isSaveScheduleError || isSaveQurbaniError) && <p className="text-sm text-mtn-red">Sorry, we were unable to update your schedule amounts</p>}

          </section>

          <GivematchShareSection schedule={schedule} platform={Variant.mtd} />
          <hr className="my-10 col-span-2 text-mtd-gray-200" />

          <section>
            <h2 className="text-mtd-blue">Here&apos;s Your Breakdown</h2>
            <div className="mt-10 grid gap-3 grid-cols-auto-min-30 md:grid-cols-5 lg:grid-cols-auto-min-30 xl:grid-cols-5">
              {splitBreakdown}
            </div>
          </section>
        </>}
      </div>
      <div className="flex flex-col">
        {isLoading && <section className="flex items-center justify-center p-7"><Spinner /></section>}
        {!isLoading && !isError && schedule !== undefined && <>

          {/* Payment details & Cancellation */}
          <Card variant="mtd" className="mt-20 -mx-6 lg:mx-0 lg:w-96 md:mt-0">
            {schedule.cardBrand !== '' && <section className="p-7">
              <div>Paying with card: {schedule.cardBrand} ending {schedule.last4CardDigits}</div>
              <div className="mt-3">Expires {schedule.cardExpiry}</div>
            </section>}
            {schedule.cardBrand === '' && <section className="p-7">
              <div>Paying with unknown card</div>
            </section>}
            {schedule.actionable && <section className="flex justify-center border-t p-7 border-mtd-gray-200">
              {!schedule.cancelled &&
                <Button loading={isCancelLoading} disabled={isCancelLoading} onClick={() => changeScheduleCancelled()} variant="mtd-tertiary" className="text-mtd-red">CANCEL
                  DONATIONS</Button>}
              {schedule.cancelled &&
                <Button loading={isCancelLoading} disabled={isCancelLoading} onClick={() => changeScheduleCancelled()} variant="mtd-primary-old">REACTIVATE</Button>}
              {isCancelError && <p className="text-sm text-mtd-red">Sorry, we were unable to do this</p>}
            </section>}
          </Card>

          {/* Cancel Qurbani */}
          {isQurbaniEditable &&
            <Card variant="mtd" className="mt-4 -mx-6 lg:mx-0 lg:w-96">
              <div className='p-7'>
                You are currently scheduled to donate your Qurbani
              </div>
              <div className='p-7 border-t border-mtd-gray-200 flex justify-center'>
                {(isSaveScheduleLoading || isSaveQurbaniLoading) ? <Spinner />
                  : <Button onClick={onClickCancelQurbani} variant="mtn-tertiary" className="text-mtd-red">
                    CANCEL QURBANI
                  </Button>
                }
              </div>
            </Card>}

          {/* Add Qurbani */}
          {canQurbaniBeAdded && charity && charity.causes.some(cause => cause.category === QurbaniCategory) &&
            <Card variant="mtd" className="mt-4 -mx-6 lg:mx-0 lg:w-96">
              <div className='p-7'>
                You are not currently scheduled to donate your Qurbani
              </div>
              <div className='p-7 border-t border-mtd-gray-200 flex justify-center'>
                <Button onClick={() => setShowQurbaniModal(true)} variant="mtn-tertiary">
                  ADD QURBANI TO SCHEDULE
                </Button>
              </div>
            </Card>}

          {/* Auto-enrol opt in */}
          <Card variant="mtd" className="mt-4 -mx-6 lg:mx-0 lg:w-96">
            {schedule.cardBrand !== '' && <section className="p-7">
              <div>You are currently <span className=' font-bold'>{schedule.auto_enroll ? ' opt-in ' : 'not opt-in'}</span>  for automation schedule for next year </div>
            </section>}
            {canScheduleBeAutoEnrolled(schedule, ActiveCampaign.myTenDays) && <section className="flex justify-center border-t p-7 border-mtn-gray-300">
              {schedule.auto_enroll && <Button loading={isAutoEnrollCancelLoading} disabled={isAutoEnrollCancelLoading} onClick={() => {
                setAutoEnrollState()
                MTDtracking.cancelAutoEnroll(user?.email, schedule.charity_name, schedule.charity_id, schedule.total, schedule.currency, schedule.causes, schedule.auto_enroll)
              }} variant="mtn-tertiary"
                className="text-mtn-red">CANCEL NEXT YEAR SCHEDULE </Button>}
              {!schedule.auto_enroll &&
                <Button loading={isAutoEnrollCancelLoading} disabled={isAutoEnrollCancelLoading} onClick={() => {
                  setAutoEnrollState()
                  MTDtracking.reactivateAutoEnroll(user?.email, schedule.charity_name, schedule.charity_id, schedule.total, schedule.currency, schedule.causes, schedule.auto_enroll)
                }} variant="mtn-tertiary">OPT-IN FOR NEXT YEAR SCHEDULE</Button>}
              {isAutoEnrollCancelError && <p className="text-sm text-mtn-red">Sorry, we were unable to do this</p>}
            </section>}
          </Card>

          {/* Update payment details */}
          {(ActiveCampaign.myTenNights === schedule?.campaign || (schedule?.campaign && isCampaignInCurrentYearOrPrevious(schedule.campaign))) && (
            <Card variant="mtd" className="mt-4 -mx-6 lg:mx-0 lg:w-96">
              <section className="flex justify-center border-t p-7 border-mtn-gray-300">
                <Button id='checkout-button' variant="mtn-tertiary" onClick={handleClickWrapper}>UPDATE PAYMENT DETAILS</Button>
              </section>
            </Card>
          )}
        </>}
      </div>
    </div>
  </Page >
}
